/**
 * useNetworkStatus - Hook to detect online/offline status
 *
 * Part of GAP-MOBILE-003: Offline Capabilities Phase 1
 * Created: 2025-12-31
 *
 * Features:
 * - Detects current online/offline status
 * - Tracks if user was recently offline
 * - Dispatches custom event when network is restored (for data refresh)
 */

import { useState, useEffect, useCallback } from 'react';

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);

  const handleOnline = useCallback(() => {
    console.log('[Network] Connection restored');
    setIsOnline(true);

    if (wasOffline) {
      // Dispatch custom event so components can refresh data
      window.dispatchEvent(new CustomEvent('network-restored'));
      console.log('[Network] Dispatched network-restored event');
    }
    setWasOffline(false);
  }, [wasOffline]);

  const handleOffline = useCallback(() => {
    console.log('[Network] Connection lost - showing cached content');
    setIsOnline(false);
    setWasOffline(true);
  }, []);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return { isOnline, wasOffline };
}

export default useNetworkStatus;
